import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendOTP, verifyOTP } from '../services/authService';
import Header from '../components/Header';
import { trackEvent, ClarityEvents } from '../utils/clarityTracking';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [requestId, setRequestId] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [timer, setTimer] = useState(0);
  const [name, setName] = useState('');

  const startTimer = () => {
    setTimer(30);
    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    if (!name.trim()) {
      setError('Please enter your name');
      setSuccessMessage('');
      return;
    }
    if (!phoneNumber || phoneNumber.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      setSuccessMessage('');
      return;
    }

    try {
      // Track login attempt
      trackEvent(ClarityEvents.LOGIN_ATTEMPT, {
        method: 'phone_otp',
        phone: phoneNumber
      });

      setIsLoading(true);
      setError('');
      setSuccessMessage('');
      const response = await sendOTP(phoneNumber);
      setRequestId(response.requestId);
      setIsOtpSent(true);
      startTimer();
      setSuccessMessage(`OTP sent to your WhatsApp (+91 ${phoneNumber})`);
    } catch (error) {
      setError('Failed to send OTP. Please try again.');
      setSuccessMessage('');
      trackEvent(ClarityEvents.LOGIN_FAILURE, {
        method: 'phone_otp',
        error: error instanceof Error ? error.message : 'Failed to send OTP',
        stage: 'otp_send'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      await verifyOTP(requestId, otp, phoneNumber);
      
      // Track successful login
      trackEvent(ClarityEvents.LOGIN_SUCCESS, {
        method: 'phone_otp',
        phone: phoneNumber
      });

      // Handle redirect after successful login
      const state = location.state as any;
      if (state?.from) {
        // If coming from ServiceSelector, redirect back with model and services
        navigate(state.from, { 
          state: { 
            model: state.model,
            selectedServices: state.selectedServices 
          } 
        });
      } else {
        // Get the return URL from query parameters or default to home
        const params = new URLSearchParams(location.search);
        const returnUrl = params.get('returnUrl') || '/';
        navigate(returnUrl);
      }
    } catch (error) {
      setError('Invalid OTP. Please try again.');
      trackEvent(ClarityEvents.LOGIN_FAILURE, {
        method: 'phone_otp',
        error: error instanceof Error ? error.message : 'Invalid OTP',
        stage: 'otp_verify'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Login" />
      
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Login to your account
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {isOtpSent
                ? 'Enter the OTP sent to your mobile number'
                : 'Enter your mobile number to continue'}
            </p>
          </div>

          <div className="mt-8 space-y-6">
            {!isOtpSent ? (
              <div>
                <div className="rounded-md shadow-sm -space-y-px">
                  <div>
                    <label htmlFor="user-name" className="sr-only">
                      Name
                    </label>
                    <input
                      id="user-name"
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-minto-500 focus:border-minto-500 focus:z-10 sm:text-sm mb-4"
                      placeholder="Enter your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone-number" className="sr-only">
                      Mobile Number
                    </label>
                    <input
                      id="phone-number"
                      type="tel"
                      pattern="[0-9]{10}"
                      maxLength={10}
                      required
                      value={phoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setPhoneNumber(value);
                      }}
                      className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-minto-500 focus:border-minto-500 focus:z-10 sm:text-sm"
                      placeholder="Enter 10-digit mobile number"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    onClick={handleSendOtp}
                    disabled={isLoading || phoneNumber.length !== 10}
                    className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                      isLoading || phoneNumber.length !== 10
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-minto-500 hover:bg-minto-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-minto-500'
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Sending OTP...
                      </div>
                    ) : (
                      'Send OTP'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="rounded-md shadow-sm -space-y-px">
                  <div>
                    <label htmlFor="otp" className="sr-only">
                      OTP
                    </label>
                    <input
                      id="otp"
                      type="text"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      required
                      value={otp}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setOtp(value);
                      }}
                      className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-minto-500 focus:border-minto-500 focus:z-10 sm:text-sm"
                      placeholder="Enter 6-digit OTP"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-gray-600">
                    {timer > 0 ? (
                      `Resend OTP in ${timer}s`
                    ) : (
                      <button
                        onClick={handleSendOtp}
                        disabled={isLoading}
                        className="text-minto-600 hover:text-minto-500"
                      >
                        Resend OTP
                      </button>
                    )}
                  </span>
                  <button
                    onClick={() => {
                      setIsOtpSent(false);
                      setOtp('');
                      setError('');
                    }}
                    className="text-sm text-minto-600 hover:text-minto-500"
                  >
                    Change Number
                  </button>
                </div>

                <div className="mt-6">
                  <button
                    onClick={handleVerifyOtp}
                    disabled={isLoading || otp.length !== 6}
                    className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                      isLoading || otp.length !== 6
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-minto-500 hover:bg-minto-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-minto-500'
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Verifying...
                      </div>
                    ) : (
                      'Verify OTP'
                    )}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 text-center text-sm text-red-600">{error}</div>
            )}
            {successMessage && (
              <div className="mt-4 text-center text-sm text-green-600">
                <i className="fab fa-whatsapp mr-1"></i>
                {successMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 